<template>
  <div
    class="overlay-screen flex w-full min-h-full flex-col"
    :class="verticalAlign === 'center' ? 'justify-center' : 'justify-start'"
    :style="screenStyle"
    data-testid="overlay-screen"
    :data-align="verticalAlign === 'center' ? 'center' : 'top'"
    :data-background="chrome.id"
  >
    <div
      class="overlay-content w-full"
      :class="padded ? 'container mx-auto px-4 py-8' : 'p-0'"
      data-testid="overlay-content"
    >
      <div class="overlay-html max-w-none" v-html="sanitized"></div>
    </div>
  </div>
</template>

<script setup>
import { computed } from "vue";
import DOMPurify from "dompurify";
import { createOverlayPurifier, sanitizeOverlayHtml } from "../utils/sanitizeOverlayHtml";
import { overlayChrome } from "../utils/overlayTemplate";

defineOptions({ name: "OverlayContent" });

const props = defineProps({
  content: {
    type: String,
    default: ""
  },
  padded: {
    type: Boolean,
    default: true
  },
  verticalAlign: {
    type: String,
    default: "top"
  },
  background: {
    type: String,
    default: "dark"
  }
});

const purify = createOverlayPurifier(DOMPurify);
const sanitized = computed(() => sanitizeOverlayHtml(props.content, purify));
const chrome = computed(() => overlayChrome(props.background));
const screenStyle = computed(() => ({
  backgroundColor: chrome.value.color,
  color: chrome.value.text,
  "--overlay-link": chrome.value.link
}));
</script>

<style scoped>
.overlay-html :deep(img) {
  max-width: 100%;
  height: auto;
  display: block;
}
.overlay-content.p-0 :deep(img) {
  width: 100%;
}
.overlay-html :deep(h1) {
  font-size: 1.75rem;
  font-weight: 700;
  margin-bottom: 0.75rem;
}
.overlay-html :deep(h2) {
  font-size: 1.35rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
}
.overlay-html :deep(p) {
  margin-bottom: 0.75rem;
  line-height: 1.5;
}
.overlay-html :deep(a),
.overlay-html :deep(a:visited) {
  color: var(--overlay-link, #5eead4);
  text-decoration: underline;
}
.overlay-html :deep(ul),
.overlay-html :deep(ol) {
  margin: 0.75rem 0 0.75rem 1.25rem;
}
.overlay-html :deep(.ql-align-center) {
  text-align: center;
}
.overlay-html :deep(.ql-align-right) {
  text-align: right;
}
.overlay-html :deep(.ql-align-justify) {
  text-align: justify;
}
</style>
